import { useEffect, useRef } from "react";
import { useEvent } from "../../contexts/EventContext";
import { useVisitorLookup } from "../../hooks/useVisitorLookup";
import { useCheckin } from "../../hooks/useCheckin";
import { VisitorInfoCard } from "../molecules/VisitorInfoCard";
import { getVisitStatus } from "../../lib/visitStatus";
import { extractPlannedSlotsForDate } from "../../lib/visitStatus";
import { fetchVisitors } from "../../lib/apiClient";

export function VisitorLookupSection({ encodedSynergyId, eventDate, eventTableKey, qrEventKey, onCheckinLoadingChange, }) {
  const { selectedEvent } = useEvent();
  const { lookupVisitor, visitor, loading, error } = useVisitorLookup();
  const { checkin, loading: checkinLoading, done } = useCheckin();
  const lastLookupKeyRef = useRef(null);               // lookupの多重実行を防ぐためのガード
  const refetchedAfterCheckinRef = useRef(false); // checkin後の再lookupを1回だけにするためのガード
  const autoCheckinRef = useRef(false);

  useEffect(() => {
    onCheckinLoadingChange?.(loading || checkinLoading);
  }, [loading, checkinLoading, onCheckinLoadingChange,]);

  // ✅ lookupキーをまとめる（重要）
  const lookupKey = `${encodedSynergyId}_${eventDate}_${qrEventKey}`;

  // イベント不一致判定
  const isEventMismatch = qrEventKey && selectedEvent?.tableKey && qrEventKey !== selectedEvent.tableKey;
  // 日付不一致判定（イベント一致が前提）
  const slots = extractPlannedSlotsForDate(visitor?.preferred_visit_date, eventDate);
  const isDateMismatch = visitor && !isEventMismatch && slots.length === 0;
  let note = "";
  if (isEventMismatch) {
    note = "来場イベント不一致";
  }
  else if (isDateMismatch) {
    note = "来場日不一致";
  }

  // 取ってきた来場者の来場状況を見て、ステータスを設定する
  const status = getVisitStatus({
    preferredVisitText: visitor?.preferred_visit_date,
    targetDate: eventDate,
    visited: visitor?.visited,
  });

  // ✅ 初回検索
  useEffect(() => {
    if (!encodedSynergyId || !eventDate || !qrEventKey) return;
    if (lastLookupKeyRef.current === lookupKey) return;

    lastLookupKeyRef.current = lookupKey;
    refetchedAfterCheckinRef.current = false;
    autoCheckinRef.current = false;

    const runLookup = async () => {
      const fetchedVisitor = await lookupVisitor({
        encodedSynergyId,
        eventDate,
        eventTableKey: qrEventKey,
      });
      if (!fetchedVisitor) return;
      
      // イベント不一致
        const isMismatch =
          qrEventKey &&
          selectedEvent?.tableKey &&
          qrEventKey !== selectedEvent.tableKey;
        if (isMismatch) return;
        const slots = extractPlannedSlotsForDate(
          fetchedVisitor.preferred_visit_date,
          eventDate
        );
        const isDateMatched = slots.length > 0;
        if (!isDateMatched) return;
        if (fetchedVisitor.visited) return;

        checkin({
          encodedSynergyId,
          historyId: fetchedVisitor.history_id,
          note: "",
        });
    };
    runLookup();
  }, [lookupKey, encodedSynergyId, eventDate, qrEventKey]);

  // ✅ checkin後1回だけ再検索
  useEffect(() => {
    if (!done) return;
    if (!encodedSynergyId || !eventDate || !qrEventKey) return;
    if (refetchedAfterCheckinRef.current) return;

    refetchedAfterCheckinRef.current = true;

    lookupVisitor({
      encodedSynergyId,
      eventDate,
      eventTableKey: qrEventKey,
    });
  }, [done]);

  return (
    <div style={{ marginTop: 16 }}>
      {loading && <p>検索中...</p>}
      {error && <p style={{ color: "red" }}>エラー: {error}</p>}

      {/* ✅ 不一致メッセージ */}
      {isEventMismatch && (
        <div
          style={{
            padding: "12px",
            borderRadius: "8px",
            background: "#fee2e2",
            color: "#991b1b",
            marginBottom: "12px",
            fontSize: "14px",
          }}
        >
          ⚠ 選択しているイベントと異なる来場者です
        </div>
      )}
      
      {/* ✅ 日付不一致メッセージ */}
      {visitor && !isEventMismatch && isDateMismatch && (
        <div
          style={{
            padding: "12px",
            borderRadius: "8px",
            background: "#fef3c7",
            color: "#92400e",
            marginBottom: "12px",
            fontSize: "14px",
          }}
        >
          ⚠ 来場予定日が選択されている日付と異なります
        </div>
      )}

      {/* visitor がある時だけカード表示 */}
      <VisitorInfoCard
        visitor={visitor}
        status={status}
        onCheckin={
          visitor ? () => checkin({
            encodedSynergyId,
            historyId: visitor.history_id,
            note,
          }) : undefined
        }
        checkinLoading={checkinLoading}
      />
      {checkinLoading && <p>受付処理中...</p>}
    </div>
  );
}